import type { PageContext } from '../../lib/types';
// Admin: runtime operations — Runs, Failed Runs, Queue, Workers, Integrations (backend §70–75, §96).
import { html, toast } from '../../lib/dom';
import { icon } from '../../components/icons';
import { pageHead, table, status, select, empty } from '../../components/ui';
import { failedRuns, queueJobs, queueCounts, setJobStatus } from '../../api/admin';
import { listRuns, runTotals, RUN_STATUSES } from '../../api/runs';
import { listProjects, listWorkspaces, getUser } from '../../api/workspace';
import { db, byId } from '../../api/core';
import { runsTable, bindExecutionDelete } from '../runs';
import { relTime, dateTime, num } from '../../lib/format';

export function renderRuns(ctx: PageContext) {
  const q = ctx.query;
  let rows = listRuns({ workspaceId: q.workspace, projectId: q.project, status: q.status, userId: q.user, q: q.q });
  if (q.provider) rows = rows.filter((r?: any) => db.node_runs.some((nr?: any) => nr.run_id === r.id && nr.provider === q.provider));
  if (q.org && q.org !== 'org_bots_and_bones') rows = [];
  const body = html`<div class="page">
    ${pageHead({ eyebrow: 'Platform admin', title: 'Runs', lede: 'Diagnose any run on the platform, including technical error details.' })}
    <div class="toolbar">
      <label class="search">${icon('search')}<input class="input sm" data-filter="q" value="${q.q || ''}" placeholder="Run #, flow…"></label>
      ${select('org', db.organizations.map((o?: any) => [o.id, o.name]), q.org, { placeholder: 'Any organization' })}
      ${select('workspace', listWorkspaces().map((w?: any) => [w.id, w.name]), q.workspace, { placeholder: 'Any workspace' })}
      ${select('project', listProjects().map((p?: any) => [p.id, p.name]), q.project, { placeholder: 'Any project' })}
      ${select('provider', db.providers.map((p?: any) => [p.name, p.name]), q.provider, { placeholder: 'Any provider' })}
      ${select('status', RUN_STATUSES.map((s?: any) => [s, s]), q.status, { placeholder: 'Any status' })}
      ${select('user', db.users.filter((u?: any) => runTotals({ userId: u.id }).runs > 0).map((u?: any) => [u.id, u.name]), q.user, { placeholder: 'Any user' })}
    </div>
    <div class="card">${rows.length ? runsTable(rows, { base: '#/admin/runs/', org: true }) : empty('No runs match. Only the demo tenant has run history in this dataset.')}</div>
  </div>`;
  return { title: 'Admin · Runs', body, mount(root: HTMLElement) { bindExecutionDelete(root, ctx.rerender); } };
}

export function renderFailed(ctx: PageContext) {
  const rows = failedRuns();
  const body = html`<div class="page">
    ${pageHead({ eyebrow: 'Platform admin', title: 'Failed Runs', lede: 'The operational inbox: what failed, where, and why.' })}
    <div class="card">${table([
      { label: 'Run', mono: true, render: (f?: any) => `#${f.run.number}` },
      { label: 'Flow', render: (f?: any) => html`<div class="col" style="gap:0"><span>${f.flow.name}</span><span class="muted" style="font-size:12px">${f.org.name}</span></div>` },
      { label: 'Failed node', render: (f?: any) => f.node?.name || '—' },
      { label: 'Provider', render: (f?: any) => f.nodeRun?.provider || '—' },
      { label: 'Error', render: (f?: any) => html`<div class="col" style="gap:0"><span class="mono" style="font-size:11px;color:var(--error)">${f.nodeRun?.error?.code}</span><span style="font-size:12px">${f.nodeRun?.error?.message}</span></div>` },
      { label: 'Time', render: (f?: any) => relTime(f.run.created_at) },
      { label: '', render: (f?: any) => f.flow ? html`<a class="btn sm" href="#/projects/${f.flow.project_id}/flows/${f.flow.id}">${icon('node')}Open flow</a>` : '' },
    ], rows, { rowHref: (f?: any) => `#/admin/runs/${f.run.id}` })}</div>
  </div>`;
  return { title: 'Admin · Failed Runs', body };
}

export function renderQueue(ctx: PageContext) {
  const st = ctx.query.status || '';
  const counts = queueCounts();
  const rows = queueJobs(st || undefined);
  const body = html`<div class="page">
    ${pageHead({ eyebrow: 'Platform admin', title: 'Queue', lede: 'AI jobs never run inside HTTP requests: API → Queue → Worker → Provider.' })}
    <div class="grid c4" style="grid-template-columns:repeat(5,minmax(0,1fr))">${Object.entries(counts).map(([k, v]: any) => html`<button class="card kpi hover" data-param="status" data-value="${k === 'completed' ? '' : k}" style="border:none;text-align:left;font:inherit;${st === k ? 'box-shadow:var(--ink) 0 0 0 1px inset' : ''}"><span class="eyebrow">${k}</span><span class="v">${num(v)}</span></button>`)}</div>
    <div class="card">${table([
      { label: 'Job', mono: true, render: (j?: any) => j.id },
      { label: 'Queue', render: (j?: any) => html`<span class="chip">${j.queue}</span>` },
      { label: 'Status', render: (j?: any) => status(j.status === 'active' ? 'running' : j.status) },
      { label: 'Run', render: (j?: any) => (j.run_id ? html`<a href="#/admin/runs/${j.run_id}" style="color:var(--fern)">#${String(j.run_id).slice(0, 8)}</a>` : html`<span class="muted">${j.note || '—'}</span>`) },
      { label: 'Node', render: (j?: any) => (j.node_run_id ? byId('nodes', byId('node_runs', j.node_run_id)?.node_id)?.name : '—') },
      { label: 'Attempts', num: true, render: (j?: any) => j.attempts },
      { label: 'Created', render: (j?: any) => relTime(j.created_at) },
      { label: '', render: (j?: any) => html`<div class="row" style="justify-content:flex-end">${j.status === 'failed' ? html`<button class="btn sm" data-job="${j.id}:waiting">Retry</button>` : ''}${['waiting', 'delayed', 'active'].includes(j.status) ? html`<button class="btn sm ghost" data-job="${j.id}:cancelled">Cancel</button>` : ''}${j.status === 'delayed' || j.status === 'cancelled' ? html`<button class="btn sm ghost" data-job="${j.id}:waiting">Requeue</button>` : ''}</div>` },
    ], rows, { empty: 'Queue is empty' })}</div>
  </div>`;
  return {
    title: 'Admin · Queue', body,
    mount: (root?: any) => root.querySelectorAll('[data-job]').forEach((b?: any) => b.addEventListener('click', () => { const [id, s] = b.dataset.job.split(':'); setJobStatus(id, s); toast(`${id} → ${s}`); ctx.rerender(); })),
  };
}

export function renderWorkers() {
  const body = html`<div class="page">
    ${pageHead({ eyebrow: 'Platform admin', title: 'Workers', lede: 'Workers execute node types through the executor registry.' })}
    <div class="card">${table([
      { label: 'Worker', mono: true, render: (w?: any) => w.name },
      { label: 'Type', render: (w?: any) => html`<span class="chip">${w.type}</span>` },
      { label: 'Status', render: (w?: any) => status(w.status) },
      { label: 'Current job', mono: true, render: (w?: any) => w.current_job || '—' },
      { label: 'Last heartbeat', render: (w?: any) => html`<span class="${w.status === 'offline' ? 'err' : ''}">${relTime(w.last_heartbeat_at)}</span>` },
      { label: 'Completed', num: true, render: (w?: any) => num(w.jobs_completed) },
      { label: 'Failed', num: true, render: (w?: any) => num(w.jobs_failed) },
      { label: 'Version', mono: true, render: (w?: any) => w.version },
    ], db.workers)}</div>
  </div>`;
  return { title: 'Admin · Workers', body };
}

export function renderIntegrations() {
  const byProvider: any = {};
  const recent = listRuns({ limit: 6 });
  db.connections.forEach((c?: any) => { (byProvider[c.provider] ||= []).push(c); });
  const body = html`<div class="page">
    ${pageHead({ eyebrow: 'Platform admin', title: 'Integrations', lede: 'Connection health across tenants and provider callback endpoints.' })}
    <div class="card">${table([
      { label: 'Integration', render: ([p]: any) => html`<strong style="font-weight:500">${p}</strong>` },
      { label: 'Connections', num: true, render: ([, cs]: any) => cs.length },
      { label: 'Active', num: true, render: ([, cs]: any) => cs.filter((c?: any) => c.status === 'active').length },
      { label: 'Needs attention', render: ([, cs]: any) => cs.filter((c?: any) => c.status !== 'active').map((c?: any) => status('failed', `${c.name}: ${c.status}`)) },
      { label: 'Last used', render: ([, cs]: any) => relTime(cs.map((c?: any) => c.last_used_at).sort().pop()) },
    ], Object.entries(byProvider))}</div>
    <section class="section"><h2>Provider callbacks</h2><div class="card">${table([
      { label: 'Endpoint', mono: true, render: (r?: any) => `/webhooks/providers/${r[0]}` },
      { label: 'Last callback', render: (r?: any) => relTime(r[1]) },
      { label: 'Idempotent duplicates dropped (24h)', num: true, render: (r?: any) => r[2] },
    ], [['fal', recent[0]?.created_at, 3], ['google', recent[2]?.created_at, 0], ['replicate', recent[5]?.created_at, 1]])}</div></section>
  </div>`;
  return { title: 'Admin · Integrations', body };
}

export { getUser, dateTime };
